import { useTranslation } from 'react-i18next';
import React, { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  TextField,
} from '@mui/material';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import client from 'api/client';
import useAuthStore from 'stores/authStore';
import { extractApiError, localizeApiMessage } from 'api/errors';
import { useTokens } from 'theme';
import AuthLayout, { FieldLabel, PasswordToggle } from './AuthLayout';

export default function AcceptInvitePage() {
  const { t: translate } = useTranslation();
  const t = useTokens();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || '';
  const navigate = useNavigate();
  const setAuth = useAuthStore((s) => s.setAuth);

  const [name, setName]       = useState('');
  const [password, setPassword] = useState('');
  const [showPw, setShowPw]   = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError]     = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);
    setLoading(true);
    try {
      const { data } = await client.post('/api/v1/auth/invitations/accept/', {
        token,
        name: name.trim() || undefined,
        password: password || undefined,
      });
      // Only a brand-new account comes back with a session; an invite for an
      // existing account just adds the membership, so keep the current session.
      if (data.access) {
        setAuth({
          accessToken:      data.access,
          refreshToken:     data.refresh,
          user:             data.user,
          membership:       data.membership,
        });
      }
      navigate('/console/dashboard', { replace: true });
    } catch (err) {
      const status = err?.response?.status;
      const detail = err?.response?.data?.detail;
      if (status === 403) {
        // SEC-02: the email already has an account — sign in first, then re-open
        // the link to join (the invite never logs you in on its own).
        setError(localizeApiMessage(detail, null, translate('shell.inviteExistingAccount')));
      } else if (status === 400) {
        setError(localizeApiMessage(detail, null, translate('shell.inviteInvalid')));
      } else if (status === 404) {
        setError(translate('shell.inviteExpired'));
      } else {
        setError(extractApiError(err, translate('shell.inviteAcceptFailed')));
      }
    } finally {
      setLoading(false);
    }
  };

  if (!token) {
    return (
      <AuthLayout title={translate('shell.inviteBroken')}>
        <Alert severity="error">{translate('shell.inviteMissingToken')}</Alert>
      </AuthLayout>
    );
  }

  return (
    <AuthLayout title={translate('shell.invited')} subtitle={translate('shell.inviteSubtitle')}>
      {error && <Alert severity="error" sx={{ mb: 2.5 }}>{error}</Alert>}

      <Box component="form" onSubmit={handleSubmit} sx={{ display: 'flex', flexDirection: 'column', gap: 2.25 }}>
        <Box>
          <FieldLabel htmlFor="invite-name">{translate('shell.yourName')}</FieldLabel>
          <TextField
            id="invite-name"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={translate('shell.fullName')}
            autoComplete="name"
            fullWidth
            autoFocus
          />
        </Box>
        <Box>
          <FieldLabel htmlFor="invite-password">{translate('shell.password')}</FieldLabel>
          <TextField
            id="invite-password"
            type={showPw ? 'text' : 'password'}
            value={password}
            onChange={(e) => setPassword(e.target.value)}
            autoComplete="new-password"
            required
            fullWidth
            InputProps={{
              endAdornment: <PasswordToggle shown={showPw} onToggle={() => setShowPw((v) => !v)} />,
            }}
          />
        </Box>
        <Button
          type="submit"
          variant="contained"
          fullWidth
          size="large"
          disabled={loading || !password}
          endIcon={loading ? null : <ArrowForwardIcon sx={{ fontSize: '18px !important' }} />}
          sx={{ mt: 0.75, minHeight: 46 }}
        >
          {loading ? <CircularProgress size={20} sx={{ color: t.onAccent }} /> : translate('shell.joinWorkspace')}
        </Button>
      </Box>
    </AuthLayout>
  );
}
